import React, { memo, useState, useEffect, ChangeEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Select, ISelectOption } from 'components/forms/Selects'
import { Autocomplete } from 'components/forms/autocomplete/DPLAutocomplete'
import { FormModal } from 'transfix-ui/components/Modals'
import { PhoneInput } from 'transfix-ui/components/PhoneInput'
import { USER_ROLES as USER_ROLES_ENUM } from 'types'
import { createChildUser, editChildUser, updateParentUserProfileDetails } from 'utils/actions/user'
import { IChildUserState } from 'utils/reducers/childUsers'
import {
  PERMITTED_USER_CREATION_TYPES,
  USER_ROLES,
  USER_STATES,
  USER_ROLE_DESCRIPTION,
} from 'apps/user/constants/user'
import {
  IUser,
  IParentUser,
  IChildUser,
  IOrgDetails,
  IUserCreationState,
  USER_TYPES,
  USER_STATES as USER_STATES_ENUM,
} from 'apps/user/types/user'
import { selectIsParentUserDeleted } from 'apps/user/utils/selectors/userProfile'
import { validateChildUser } from 'apps/user/utils/validator'
import { TextInput } from 'components/atoms'

interface IUserAccountModalProps {
  closeModalHandler: () => void
  parentUser: IParentUser
  showModal: boolean
  user?: IChildUser
}

interface IStore {
  childUsers: IChildUserState
  userCreate: IUserCreationState
}

export const UserAccountModal = memo<IUserAccountModalProps>(
  ({ closeModalHandler, parentUser, showModal, user }) => {
    const [userType, setUserType] = useState<USER_TYPES | undefined>()
    const [role, setRole] = useState<USER_ROLES_ENUM | undefined>()
    const [stateName, setStateName] = useState<USER_STATES_ENUM | undefined>()
    const [org, setOrg] = useState<string>('')
    const [data, setData] = useState<Partial<IUser>>({
      applicationType: 'CarrierApplication', // note: we only support this now
      applicationId: '',
    })
    const [errors, setErrors] = useState<{ [key: string]: string }>({})

    // local form state
    const [userRequiresUpdate, setUserRequiresUpdate] = useState<boolean>(false)
    const [showError, setShowError] = useState<boolean>(false)

    // redux state
    const dispatch = useDispatch()
    const isParentUserDeleted = useSelector(selectIsParentUserDeleted)

    const {
      error,
      fetching,
      isCreatingUser: isUpdatingUser,
      userCreateError: userUpdateError,
      users,
    } = useSelector<IStore, IChildUserState & IUserCreationState>(({ childUsers, userCreate }) => ({
      ...childUsers,
      ...userCreate,
    }))

    const dismissModal = () => {
      setUserType(undefined)
      setRole(undefined)
      setStateName(undefined)
      setOrg('')
      setData({
        applicationType: 'CarrierApplication',
        applicationId: '',
      })
      setErrors({})
      setShowError(false)

      closeModalHandler()
    }

    const updateUser = (userToUpdate: IChildUser): void => {
      setUserRequiresUpdate(false)
      const { parentUserId } = userToUpdate
      const newUser = {
        id: parentUserId,
        cellPhone: data.cellPhone || parentUser.cellPhone,
        phone: data.phone || parentUser.phone,
        phoneExt: data.phoneExt || parentUser.phoneExt,
        email: data.email || parentUser.email,
      }
      dispatch(updateParentUserProfileDetails(newUser))
    }

    useEffect(() => {
      if (user) {
        setOrg(user.companyName)
        setRole(user.role)
        setStateName(user.stateName)
        setUserType(user.userType)
        setData({
          ...user,
        })
      }
    }, [user])

    useEffect(() => {
      const userSaved = users.find(savedUser => savedUser.applicationId === data.applicationId)

      if (userSaved) {
        if (userRequiresUpdate) {
          updateUser(userSaved)
        }
        if (!userRequiresUpdate && !userUpdateError && !isUpdatingUser) {
          dismissModal()
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, userRequiresUpdate, userUpdateError, isUpdatingUser])

    // inferred local state
    const userIsDriverOrOwnerOperator = !!(
      userType === USER_TYPES.driver || userType === USER_TYPES.ownerOperator
    )
    const userIsDispatcherOrOwnerOperator = !!(
      userType === USER_TYPES.dispatcher || userType === USER_TYPES.ownerOperator
    )
    const showCellPhone = userIsDriverOrOwnerOperator && !parentUser.cellPhone
    const showEmail = userIsDispatcherOrOwnerOperator && !parentUser.email
    const showPhone = userIsDispatcherOrOwnerOperator && !parentUser.phone

    const autoCompleteChangeHandler = ({
      fullSelection: { id, name },
    }: {
      fullSelection: IOrgDetails
    }): void => {
      const orgFields: { [key: string]: string | number } = {}
      orgFields.applicationId = id

      setOrg(name)
      setData({ ...data, ...orgFields })
    }

    const handleUserTypeSelection = (selectedUserType: ISelectOption<USER_TYPES>): void => {
      const type = selectedUserType.value

      setUserType(selectedUserType.value)

      // set form field displayed
      if (type === USER_TYPES.driver) {
        setRole(USER_ROLES_ENUM.employee)
      }
    }

    const inputChangeHandler = ({
      target: { name, value },
    }: ChangeEvent<HTMLInputElement>): void => {
      const newKeyValuePair: { [key: string]: string } = {}
      newKeyValuePair[name] = value
      setData({ ...data, ...newKeyValuePair })
    }

    const handleRoleSelection = (selectedRole: ISelectOption<USER_ROLES_ENUM>): void => {
      setRole(selectedRole.value)
    }

    const handleStateSelection = (selectedState: ISelectOption<USER_STATES_ENUM>): void => {
      setStateName(selectedState.value)
    }

    // TODO: Refactor - there's a better way to update related data, requires larger refactor
    // note: an update to parent user is only necessary when the usertype needs
    // extra values (phone, phoneExt, cellPhone, email)
    const doesUserRequireUpdate = (): boolean =>
      !!(isParentUserDeleted || showEmail || showPhone || showCellPhone)

    const saveChildUser = (): void => {
      setShowError(true)
      const payload = {
        ...(user || {}),
        ...data,
        userType,
        role,
        stateName,
        parentUserId: parentUser.id,
      }
      const payloadWithParentUser = { ...parentUser, ...payload }
      setUserRequiresUpdate(doesUserRequireUpdate())

      try {
        validateChildUser(payloadWithParentUser)
        if (!user) {
          dispatch(createChildUser(payload))
        }
        if (user) {
          dispatch(editChildUser(payload))
        }
      } catch (errs) {
        setErrors(errs)
      }
    }

    return (
      <FormModal
        size={5}
        error={showError ? error || userUpdateError : undefined}
        open={showModal}
        onClose={dismissModal}
        title={`${user ? 'Edit' : 'Add'} Carrier Account`}
        actions={[
          {
            label: 'Cancel',
            type: 'dismiss',
            disabled: fetching || isUpdatingUser,
            dataTest: 'cancel',
          },
          {
            label: 'Save',
            type: 'confirm',
            disabled: fetching || isUpdatingUser,
            action: saveChildUser,
            dataTest: 'save-user',
          },
        ]}>
        <p className='mb4'>
          {user ? 'Edit' : 'Add'}&nbsp;
          <span className='b'>
            {parentUser.firstName} {parentUser.lastName}
          </span>
          {user ? "'s user type and permissions for this carrier" : ' as a user to another carrier'}
          .
        </p>
        <>
          <div className='row col-xs-12 col-md-8 mb2'>
            {user && (
              <>
                <p className='text-caption'>Carrier</p>
                <p>
                  <a
                    href={`${__ROOT__}/carriers/${user.companyId}`}
                    target='_blank'
                    rel='noreferrer noopener'>
                    <strong>{user.companyName}</strong>
                  </a>
                </p>
              </>
            )}
            {!user && (
              <Autocomplete
                large
                required
                dataTest='search-carrier'
                id='search-carrier'
                label='Carrier'
                onChange={autoCompleteChangeHandler}
                placeholder='Search for a carrier'
                type='carrier'
                value={org}
                validationMessage={errors?.applicationId}
                invalid={!!errors?.applicationId}
              />
            )}
          </div>
          {!user && (
            <p className='col-xs-12 mb4 text-caption'>
              Enter an MC or DOT number and select a carrier.
            </p>
          )}
        </>

        <div className='row mb4'>
          <div className='col-xs-12 col-md-6 col-lg-5'>
            <Select
              large
              required
              label='User Type'
              placeholder='Select a user type'
              value={userType}
              onChange={handleUserTypeSelection}
              options={PERMITTED_USER_CREATION_TYPES.carrier}
              validationMessage={errors?.userType}
              invalid={!!errors?.userType}
              dataTest='user-type'
            />
          </div>
        </div>

        {showEmail && (
          <div className='row mb4'>
            <div className='mb2 col-xs-12 col-md-6 col-lg-5'>
              <TextInput
                large
                required
                label='Email'
                placeholder='Enter email address'
                value={data?.email}
                name='email'
                onChange={inputChangeHandler}
                invalid={!!errors?.email}
                dataTest='user-email'
              />
            </div>
            <p className='col-xs-12 ml2 text-caption'>
              An email address is required for this type of account.
            </p>
          </div>
        )}

        {showPhone && (
          <div className='row pa0 mb4'>
            <div className='col-xs-8 col-md-6 col-lg-4 col-xl-3'>
              <PhoneInput
                large
                required
                label='Phone'
                value={data?.phone}
                name='phone'
                onChange={inputChangeHandler}
                validationMessage={errors?.phone}
                invalid={!!errors?.phone}
                dataTest='user-phone'
              />
            </div>
            <div className='col-xs-4 col-md-3 col-lg-2'>
              <TextInput
                large
                label='Ext'
                placeholder='000'
                value={data?.phoneExt}
                inputMode='numeric'
                maxLength={3}
                validationMessage={errors?.phoneExt}
                invalid={!!errors?.phoneExt}
                name='phoneExt'
                onChange={inputChangeHandler}
                dataTest='user-phone-ext'
              />
            </div>
            <p className='col-xs-12 ml2 text-caption'>
              A phone number is required for this type of account.
            </p>
          </div>
        )}

        {showCellPhone && (
          <div className='row mb4'>
            <div className='mb2 col-xs-8 col-md-6 col-lg-4 col-xl-3'>
              <PhoneInput
                large
                required
                value={data?.cellPhone}
                label='Cell phone'
                name='cellPhone'
                onChange={inputChangeHandler}
                validationMessage={errors?.cellPhone}
                invalid={!!errors?.cellPhone}
                dataTest='user-cell-phone'
              />
            </div>
            <p className='col-xs-12 ml2 text-caption'>
              A cell phone number is required for this type of account.
            </p>
          </div>
        )}

        <div className='row col-xs-12 mb4'>
          {userType !== USER_TYPES.driver ? (
            <>
              <div className='col-xs-8 col-md-5 pa0'>
                <Select
                  large
                  required
                  label='Role'
                  value={role}
                  onChange={handleRoleSelection}
                  placeholder='Select a Role'
                  options={USER_ROLES}
                  validationMessage={errors?.role}
                  invalid={!!errors?.role}
                  dataTest='user-role'
                />
              </div>
              <p className='col-xs-12 text-caption'>{USER_ROLE_DESCRIPTION[role as string]}</p>
            </>
          ) : (
            <div className='col-xs-12 pa0'>
              <p className='text-caption required'>Role</p>
              <p>Employee</p>
            </div>
          )}
        </div>

        {user && (
          <div className='row col-xs-12 mb4'>
            <div className='col-xs-8 col-md-5 pa0'>
              <Select
                large
                required
                label='Status'
                value={stateName}
                onChange={handleStateSelection}
                placeholder='Select a status'
                options={USER_STATES}
                validationMessage={errors?.stateName}
                invalid={!!errors?.stateName}
              />
            </div>
          </div>
        )}
      </FormModal>
    )
  }
)
